import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, FileText, Download, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Logo } from "@/components/Navbar";
import Pricing from "@/components/Pricing";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { fmtDate, fmtEuro } from "@/lib/format";

const PLAN_LABELS = { trial: "Essai gratuit", trialing: "Essai gratuit", none: "Sans formule", essentiel: "Essentiel", pro: "Pro", premium: "Premium" };
const INVOICE_STATUS = { paid: "Payée", open: "En attente", draft: "Brouillon", void: "Annulée", uncollectible: "Impayée" };

export default function Billing() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [yearly, setYearly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [acting, setActing] = useState(false);

  const isActive = ["trialing", "trial", "essentiel", "pro", "premium"].includes(user?.plan);

  const loadSub = useCallback(() => {
    api.get("/subscription").then(({ data }) => setSub(data)).catch(() => {});
    api.get("/subscription/invoices").then(({ data }) => setInvoices(data.invoices || [])).catch(() => {});
  }, []);
  useEffect(() => { loadSub(); }, [loadSub]);

  const checkout = async (lookup) => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookup, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Impossible de démarrer le paiement.");
      setLoading(false);
    }
  };

  const doCancel = async () => {
    setActing(true);
    try {
      const { data } = await api.post("/subscription/cancel");
      toast.success(data.mode === "immediate"
        ? "Essai résilié. Aucun prélèvement ne sera effectué."
        : "Abonnement résilié. Vous gardez l'accès jusqu'à la fin de la période.");
      await checkAuth();
      loadSub();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Résiliation impossible pour le moment.");
    } finally { setActing(false); setConfirmCancel(false); }
  };

  const doResume = async () => {
    setActing(true);
    try {
      await api.post("/subscription/resume");
      toast.success("Abonnement réactivé.");
      await checkAuth();
      loadSub();
    } catch (e) {
      toast.error("Réactivation impossible pour le moment.");
    } finally { setActing(false); }
  };

  const plan = user?.plan;
  const trialing = plan === "trialing";
  const canceled = sub?.cancel_at_period_end;
  const endDate = trialing ? sub?.trial_ends_at : sub?.current_period_end;
  const chosenLabel = PLAN_LABELS[sub?.chosen_plan] || PLAN_LABELS[plan] || "";

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b border-border">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Button variant="ghost" size="sm" onClick={() => navigate(isActive ? "/dashboard" : "/")} data-testid="billing-back-btn"><ArrowLeft className="w-4 h-4 mr-1.5" /> {isActive ? "Mon espace" : "Accueil"}</Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-10">
        {/* Current subscription status */}
        {isActive && sub && (
          <div className="mb-10 rounded-2xl border border-border bg-white p-6" data-testid="subscription-status-card">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-xs font-mono uppercase tracking-wide text-slate-400">Votre abonnement</p>
                <p className="font-display text-2xl font-extrabold text-slate-900 mt-1" data-testid="current-plan-label">{PLAN_LABELS[plan] || "Formule"}</p>
                {trialing && !canceled && (
                  <p className="text-sm text-slate-600 mt-2 flex items-start gap-2" data-testid="status-trialing">
                    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <span>Essai gratuit jusqu'au <b>{fmtDate(endDate)}</b>. Sans résiliation, votre formule <b>{chosenLabel}</b> sera facturée automatiquement à cette date.</span>
                  </p>
                )}
                {!trialing && !canceled && (
                  <p className="text-sm text-slate-600 mt-2 flex items-start gap-2" data-testid="status-active">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                    <span>Abonnement actif.{endDate ? <> Prochain renouvellement le <b>{fmtDate(endDate)}</b>.</> : null}</span>
                  </p>
                )}
                {canceled && (
                  <p className="text-sm text-slate-600 mt-2 flex items-start gap-2" data-testid="status-canceled">
                    <AlertTriangle className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                    <span>Résilié. Vous gardez l'accès jusqu'au <b>{fmtDate(endDate)}</b>, puis aucun prélèvement.</span>
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                {!canceled ? (
                  <Button variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setConfirmCancel(true)} disabled={acting} data-testid="cancel-subscription-btn">
                    {trialing ? "Annuler mon essai" : "Se désabonner"}
                  </Button>
                ) : (
                  <Button className="rounded-full" onClick={doResume} disabled={acting} data-testid="resume-subscription-btn">
                    {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Réactiver mon abonnement"}
                  </Button>
                )}
              </div>
            </div>

            {/* Invoices */}
            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-xs font-mono uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-1.5"><FileText className="w-4 h-4" /> Mes factures</p>
              {invoices.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="no-invoices">Aucune facture pour le moment. Vos factures apparaîtront ici après le premier prélèvement.</p>
              ) : (
                <div className="divide-y divide-border" data-testid="invoices-list">
                  {invoices.map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between gap-3 py-2.5" data-testid="invoice-row">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800">{fmtDate(inv.date)}</p>
                        <p className="text-xs text-slate-400">{inv.number || inv.id} · {INVOICE_STATUS[inv.status] || inv.status}</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-sm font-mono font-semibold text-slate-900">{fmtEuro(inv.amount)}</span>
                        {inv.pdf && (
                          <a href={inv.pdf} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80" data-testid="invoice-pdf-link" title="Télécharger la facture">
                            <Download className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="text-center max-w-2xl mx-auto mb-10">
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900">
            {isActive ? "Changer de formule" : "Choisissez votre formule"}
          </h1>
          <p className="text-slate-500 mt-2">
            {sub?.trial_used
              ? "Vous avez déjà utilisé votre essai gratuit : l'abonnement démarre dès la validation."
              : "Carte demandée pour démarrer l'essai — 0€ aujourd'hui. L'abonnement ne démarre qu'après vos 7 jours d'essai, et reste résiliable en un clic. Paiement sécurisé par Stripe."}
          </p>
        </div>
        <Pricing yearly={yearly} setYearly={setYearly} onSelect={(lookup) => !loading && checkout(lookup)} ctaLabel={isActive ? "Choisir cette formule" : (sub?.trial_used ? "Souscrire maintenant" : "Démarrer mon essai gratuit")} activePlan={sub?.chosen_plan || user?.plan} noTrial={!!sub?.trial_used} />
        <p className="text-center text-xs text-slate-400 mt-8 flex items-center justify-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Paiement sécurisé par Stripe. En test : carte 4242 4242 4242 4242, date future, cryptogramme au choix.</p>
      </main>

      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent data-testid="cancel-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{trialing ? "Annuler votre essai gratuit ?" : "Se désabonner ?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {trialing
                ? "Votre essai sera arrêté immédiatement, aucun montant ne sera prélevé, et vous perdrez l'accès à votre espace."
                : "Votre abonnement restera actif jusqu'à la fin de la période déjà payée, puis sera résilié sans nouveau prélèvement."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-dialog-dismiss">Revenir</AlertDialogCancel>
            <AlertDialogAction onClick={doCancel} disabled={acting} className="bg-red-600 hover:bg-red-700" data-testid="cancel-dialog-confirm">
              {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : (trialing ? "Oui, annuler mon essai" : "Oui, me désabonner")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
